import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartPulse } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Caption, Rating, Spinner } from "@/components/Common";
import { WbCard } from "@/components/wb/WbUi";
import { refreshWbStatus } from "@/components/WbNav";
import { api, errMsg } from "@/lib/api";

export const WellbeingOverview = ({ onSaved }) => {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ mood: 3, energy: 3, stress: 3, note: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/wellbeing").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/wellbeing/checkin", f); toast.success("Check-in saved. Thank you for taking a moment."); setF({ ...f, note: "" }); load(); refreshWbStatus(); onSaved?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return <Spinner className="h-6 w-6 text-[#D4AF37]" />;
  const history = [...(data.history || [])].sort((a, b) => String(a.week).localeCompare(String(b.week)));
  return (
    <div className="space-y-6">
      <WbCard><Caption className="mb-2">Insight</Caption><p className="text-sm leading-relaxed text-zinc-300" data-testid="wellbeing-insight">{data.insight?.message || "Check in for a few weeks to see gentle patterns here."}</p></WbCard>
      <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
        <WbCard data-testid="checkin-card">
          <div className="mb-5 flex items-center justify-between"><h3 className="font-wb text-lg text-zinc-100">This week's check-in</h3><HeartPulse className="h-5 w-5 text-[#D4AF37]" /></div>
          {data.this_week && <p className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300" data-testid="checked-in-note">You've checked in this week. Saving again updates it.</p>}
          <div className="space-y-5">
            {["mood", "energy", "stress"].map((k) => <div key={k}><Caption className="mb-2 capitalize">{k}</Caption><Rating value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`rating-${k}`} /></div>)}
            <Textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Anything on your mind? (optional)" className="bg-[#09090B]" data-testid="checkin-note" />
            <Button onClick={submit} disabled={busy} className="w-full rounded-full" data-testid="checkin-submit-button">{busy ? <Spinner /> : "Save check-in"}</Button>
          </div>
        </WbCard>
        <WbCard className="min-w-0">
          <h3 className="mb-4 font-wb text-lg text-zinc-100">Weekly trend</h3>
          <div className="h-72" data-testid="wellbeing-chart">
            <ResponsiveContainer>
              <LineChart data={history} margin={{ left: -20, right: 10 }}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
                <XAxis dataKey="week" stroke="#71717A" fontSize={11} />
                <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8 }} />
                <Legend />
                <Line type="monotone" dataKey="mood" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="energy" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="stress" stroke="#F87171" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </WbCard>
      </div>
    </div>
  );
};
